import { api, type AssistantAnswer, type AssistantTurn } from './api';
import { esc, html } from './ui';

/**
 * The helper, docked.
 *
 * A third column beside the work rather than a bubble over it, because almost
 * every answer ends in "the field is on this screen, here" and a floating panel
 * covers the thing being pointed at. On a phone the same panel is a sheet, so
 * the record stays visible above it.
 *
 * It carries the record behind it as a chip that can be seen and removed. That
 * chip is what makes "what is due on it?" answerable at all, and showing it is
 * the difference between context and a guess the person cannot inspect.
 *
 * The conversation lives here and is posted back whole. Nothing is stored on
 * the server: no table of what anybody asked, and no second copy of maintenance
 * data somewhere nobody thought to secure.
 */

interface Ctx {
  aircraftId?: string;
  registration?: string;
  /** What the screen is, for the chip — "Overview", "Tech log". */
  where?: string;
  path: string;
}

interface Entry {
  role: 'user' | 'assistant' | 'error';
  text: string;
  links?: AssistantAnswer['links'];
  sources?: AssistantAnswer['sources'];
}

const state: {
  open: boolean;
  ready: boolean | null;
  language: string;
  entries: Entry[];
  asking: boolean;
  /** Cleared by the person, not by navigation — see `context`. */
  dropped: boolean;
  ctx: Ctx;
} = {
  open: false,
  ready: null,
  language: 'en',
  entries: [],
  asking: false,
  dropped: false,
  ctx: { path: '/dashboard' },
};

let host: HTMLElement | null = null;

/**
 * What people on this screen have actually got stuck on.
 *
 * Not "How can I help you today?". An empty panel that offers nothing is a
 * panel nobody types into, and these four are the questions this application
 * has genuinely produced.
 */
function starters(ctx: Ctx): Array<{ group: string; q: string }> {
  if (ctx.aircraftId) {
    return [
      { group: 'Aircraft records', q: 'How do I set the current airframe hours?' },
      { group: 'Planning', q: `What is due on ${ctx.registration ?? 'this aircraft'} in the next 90 days?` },
      { group: 'Tech log', q: 'Why can’t I enter more than 24 hours of block time?' },
      { group: 'History', q: 'Who last changed this aircraft’s hours, and why?' },
    ];
  }
  if (ctx.path.startsWith('/planning')) {
    return [
      { group: 'Planning', q: 'What is overdue across the fleet?' },
      { group: 'Work packs', q: 'How do I build a work pack from what is due?' },
      { group: 'Estimates', q: 'Which lines on an estimate are marked mandatory, and why?' },
    ];
  }
  if (ctx.path.startsWith('/techlog')) {
    return [
      { group: 'Tech log', q: 'Why can’t I enter more than 24 hours of block time?' },
      { group: 'Tech log', q: 'I made a mistake on a signed flight. How do I fix it?' },
      { group: 'Aircraft records', q: 'The airframe hours look wrong. Where do I correct them?' },
    ];
  }
  return [
    { group: 'Fleet', q: 'What needs attention across the fleet?' },
    { group: 'Aircraft records', q: 'How do I set an aircraft’s current airframe hours?' },
    { group: 'Getting around', q: 'What is the difference between a work pack and a work order?' },
  ];
}

/**
 * A safe subset of markdown.
 *
 * The model writes short answers with bold, numbered steps and the occasional
 * `code` span, and every one of those carries meaning: "Correct the record" in
 * bold is the heading to look for. Everything is escaped first and only these
 * shapes are put back, so nothing the model writes can become markup.
 */
function render(text: string): string {
  const inline = (s: string) =>
    esc(s)
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  const out: string[] = [];
  let list: 'ol' | 'ul' | null = null;

  const close = () => {
    if (list) out.push(`</${list}>`);
    list = null;
  };

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      close();
      continue;
    }

    const ordered = /^(\d+)[.)]\s+(.*)$/.exec(line);
    const bulleted = /^[-*•]\s+(.*)$/.exec(line);

    if (ordered) {
      if (list !== 'ol') {
        close();
        out.push('<ol>');
        list = 'ol';
      }
      out.push(`<li>${inline(ordered[2])}</li>`);
    } else if (bulleted) {
      if (list !== 'ul') {
        close();
        out.push('<ul>');
        list = 'ul';
      }
      out.push(`<li>${inline(bulleted[1])}</li>`);
    } else {
      close();
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  close();
  return out.join('');
}

/**
 * Where the answer came from.
 *
 * The manual chip is a link, and it names the page: the obvious next thing
 * after reading a three-line answer drawn from a page is to read the page, and
 * "Manual" alone made that a hunt through twenty of them. The records chip is
 * not a link — there is no one screen the figures came from, and a link that
 * guessed at one would be worse than none.
 *
 * `src-doc` and `src-live` rather than `doc` and `live`: `.doc` is also a
 * document row on the extraction screen, declared later in the stylesheet, and
 * it was winning — the manual chip came out nearly twice the height of the one
 * beside it.
 */
const sourceChip = (s: { kind: string; label: string; path?: string }) =>
  s.kind === 'records'
    ? html`<span class="src src-live">◉ ${esc(s.label)}</span>`
    : html`<a class="src src-doc" href="${esc(s.path ?? '/manual')}">▤ ${esc(s.label)}</a>`;

function entryHtml(e: Entry): string {
  if (e.role === 'user') {
    return html`<div class="msg me"><div class="bubble">${esc(e.text)}</div></div>`;
  }
  if (e.role === 'error') {
    return html`<div class="msg ai err"><div class="bubble">${esc(e.text)}</div></div>`;
  }

  return html`
    <div class="msg ai">
      <span class="lbl">FlyPack CAMO</span>
      <div class="bubble">
        ${render(e.text)}
        ${(e.links ?? [])
          .map(
            (l) =>
              html`<a class="goto" href="${esc(l.path)}" data-goto
                ><span class="arw">↳</span> ${esc(l.label)}</a
              >`,
          )
          .join('')}
        ${e.sources?.length
          ? html`<div class="prov"><span class="lbl">From</span>${e.sources.map(sourceChip).join('')}</div>`
          : ''}
      </div>
    </div>
  `;
}

function threadHtml(): string {
  if (state.entries.length) {
    return (
      state.entries.map(entryHtml).join('') +
      (state.asking
        ? html`<div class="msg ai"><span class="lbl">FlyPack CAMO</span>
            <div class="bubble think"><i></i><i></i><i></i></div></div>`
        : '')
    );
  }

  return html`
    <div class="msg ai">
      <div class="bubble">
        <p>
          I can explain how FlyPack CAMO works, and read your organisation’s records back to you.
          I cannot change anything.
        </p>
      </div>
    </div>
    <div>
      <span class="lbl">People on this screen have asked</span>
      <div class="starters">
        ${starters(state.ctx)
          .map(
            (s) => html`<button class="starter" type="button" data-starter="${esc(s.q)}">
              <span class="lbl">${esc(s.group)}</span>${esc(s.q)}
            </button>`,
          )
          .join('')}
      </div>
    </div>
  `;
}

function view(): string {
  const c = state.ctx;
  const showing = c.aircraftId && !state.dropped;

  return html`
    <aside class="dock" aria-label="Ask FlyPack CAMO">
      <div class="dock-h">
        <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 2.5 13.4 9l7.1 2.4v1.7l-7.1-1v4.3l2.4 1.8v1.4L12 18.9l-3.8.7v-1.4l2.4-1.8v-4.3l-7.1 1v-1.7L10.6 9 12 2.5Z"
            fill="currentColor"
            opacity=".75"
          />
        </svg>
        <span class="t">Ask FlyPack CAMO</span>
        <span class="pill p-neutral" title="It reads what you can read. It cannot change anything."
          >🔒 Read-only</span
        >
        <button class="btn gh sm x" type="button" data-close aria-label="Close the helper">✕</button>
      </div>

      ${showing
        ? html`<div class="ctx">
            <span class="lbl">Looking at</span>
            <span class="ctxchip">
              <span class="r">${esc(c.registration ?? '')}</span>${c.where ? ` · ${esc(c.where)}` : ''}
              <button type="button" data-drop title="Stop using this record">✕</button>
            </span>
          </div>`
        : ''}

      <div class="thread" data-thread>${threadHtml()}</div>

      <div class="composer">
        <div class="cbox">
          <textarea
            data-ask
            rows="1"
            placeholder="Ask a question…"
            aria-label="Ask a question"
          ></textarea>
          <button class="send" type="button" data-send aria-label="Send" ${state.asking ? 'disabled' : ''}>↑</button>
        </div>
        <div class="cfoot">
          <span class="chip acc">${esc(state.language.slice(0, 2).toUpperCase())}</span>
          <span class="tiny dim">Answers in your organisation’s language</span>
        </div>
      </div>
    </aside>
  `;
}

/** Where the aircraft chip comes from. Called by the router on every render. */
export function setContext(ctx: Ctx): void {
  const changed = ctx.aircraftId !== state.ctx.aircraftId;
  state.ctx = ctx;
  // Moving to a different aircraft is a new subject, so a chip dropped on the
  // last one should not go on suppressing this one.
  if (changed) state.dropped = false;
  if (state.open) paint();
}

/** Whether the installation has a helper at all. Cached: it cannot change mid-session. */
export async function assistantAvailable(): Promise<boolean> {
  if (state.ready !== null) return state.ready;
  try {
    const greeting = await api.assistant.greeting();
    state.ready = greeting.available;
    state.language = greeting.language;
  } catch {
    // A server without the endpoint, or an old one. No dock, no error: this is
    // help, and failing to offer it must not look like the application broke.
    state.ready = false;
  }
  return state.ready;
}

export const assistantOpen = (): boolean => state.open;

export function toggleAssistant(): void {
  state.open ? closeAssistant() : openAssistant();
}

export function openAssistant(): void {
  state.open = true;
  paint();
  requestAnimationFrame(() => host?.querySelector<HTMLTextAreaElement>('[data-ask]')?.focus());
}

export function closeAssistant(): void {
  state.open = false;
  paint();
}

/**
 * Draw, or remove.
 *
 * The dock is a grid child of `.app`, so opening it also has to widen the grid
 * — `.docked` is what makes the third column exist. Doing that here rather than
 * in the shell means the dock survives navigation without being re-created,
 * and the conversation survives with it.
 */
function paint(): void {
  const app = document.querySelector<HTMLElement>('.app');
  if (!app) return;

  app.classList.toggle('docked', state.open);
  document.querySelector('.askbtn')?.classList.toggle('on', state.open);
  document.querySelector('.askbtn')?.setAttribute('aria-expanded', String(state.open));

  if (!state.open) {
    host?.remove();
    host = null;
    return;
  }

  if (!host) {
    host = document.createElement('div');
    host.style.display = 'contents';
  }

  // Re-attach rather than assume it is still attached.
  //
  // The router redraws the whole shell on every navigation, which takes `.app`
  // and everything under it — this host included. The host object survived in
  // this module, so the old code saw a truthy `host` and appended nothing; the
  // grid grew its third column and there was nothing in it. That is exactly
  // what "Take me there" looked like from the outside: the aircraft opened and
  // the helper vanished into a blank strip. Detached nodes keep their children,
  // so re-appending brings the conversation and whatever was half-typed back
  // with it.
  if (host.parentElement !== app) app.append(host);

  // Keep what has been typed across a repaint — the context chip changing
  // under somebody mid-sentence should not take the sentence with it.
  const typed = host.querySelector<HTMLTextAreaElement>('[data-ask]')?.value ?? '';
  host.innerHTML = view();
  const box = host.querySelector<HTMLTextAreaElement>('[data-ask]');
  if (box) box.value = typed;

  bind();
  const thread = host.querySelector<HTMLElement>('[data-thread]');
  if (thread) thread.scrollTop = thread.scrollHeight;
}

function bind(): void {
  if (!host) return;

  host.querySelector('[data-close]')?.addEventListener('click', closeAssistant);
  host.querySelector('[data-drop]')?.addEventListener('click', () => {
    state.dropped = true;
    paint();
  });

  host.querySelectorAll<HTMLButtonElement>('[data-starter]').forEach((b) =>
    b.addEventListener('click', () => void send(b.dataset.starter!)),
  );

  const box = host.querySelector<HTMLTextAreaElement>('[data-ask]');
  host.querySelector('[data-send]')?.addEventListener('click', () => void send(box?.value ?? ''));

  box?.addEventListener('keydown', (e) => {
    // Enter sends, shift+enter breaks the line — the shape everybody already
    // has in their fingers from every other message box.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(box.value);
    }
  });

  box?.addEventListener('input', () => {
    box.style.height = 'auto';
    box.style.height = `${Math.min(box.scrollHeight, 120)}px`;
  });
}

async function send(question: string): Promise<void> {
  const text = question.trim();
  if (!text || state.asking) return;

  state.entries.push({ role: 'user', text });
  state.asking = true;
  const box = host?.querySelector<HTMLTextAreaElement>('[data-ask]');
  if (box) box.value = '';
  paint();

  try {
    const answer = await api.assistant.ask({
      // Errors are ours, not part of the conversation: sending "the helper
      // could not answer that" back as an assistant turn teaches it to
      // apologise for something it never said.
      messages: state.entries
        .filter((e) => e.role !== 'error')
        .map((e) => ({ role: e.role, content: e.text }) as AssistantTurn),
      aircraftId: state.dropped ? undefined : state.ctx.aircraftId,
      path: state.ctx.path,
    });

    state.entries.push({
      role: 'assistant',
      text: answer.text,
      links: answer.links,
      sources: answer.sources,
    });
  } catch (err) {
    state.entries.push({
      role: 'error',
      text: err instanceof Error ? err.message : 'The helper could not answer that.',
    });
  } finally {
    state.asking = false;
    paint();
  }
}
